import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CompanyBillingInfo {
  id: string;
  name: string;
  status: string;
  doc_cnpj: string | null;
  plan_name: string | null;
  plan_status: string | null;
  total_invoices: number;
  paid_count: number;
  pending_count: number;
  overdue_count: number;
  total_amount_cents: number;
  paid_amount_cents: number;
}

export function useCompaniesWithBillingInfo() {
  return useQuery({
    queryKey: ['companies-billing-info'],
    queryFn: async () => {
      // Fetch companies with their plans and invoices
      const { data: companies, error } = await supabase
        .from('companies')
        .select(`
          id,
          name,
          status,
          doc_cnpj,
          company_system_plans(
            plan_id,
            status,
            plans(name)
          ),
          invoices(
            id,
            status,
            amount_cents,
            due_date
          )
        `)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;

      const now = new Date();

      // Process data to calculate totals
      const processed: CompanyBillingInfo[] = (companies || []).map((company: any) => {
        const invoices = company.invoices || [];
        const activePlan = company.company_system_plans?.find((p: any) => p.status === 'active');

        return {
          id: company.id,
          name: company.name,
          status: company.status,
          doc_cnpj: company.doc_cnpj,
          plan_name: activePlan?.plans?.name || null,
          plan_status: activePlan?.status || null,
          total_invoices: invoices.length,
          paid_count: invoices.filter((i: any) => i.status === 'paid').length,
          pending_count: invoices.filter((i: any) => i.status === 'pending').length,
          overdue_count: invoices.filter((i: any) => 
            i.status === 'pending' && new Date(i.due_date) < now
          ).length,
          total_amount_cents: invoices.reduce((sum: number, i: any) => sum + (i.amount_cents || 0), 0),
          paid_amount_cents: invoices
            .filter((i: any) => i.status === 'paid')
            .reduce((sum: number, i: any) => sum + (i.amount_cents || 0), 0),
        };
      });

      // Sort by companies that have invoices first, then by name
      return processed.sort((a, b) => {
        if (a.total_invoices > 0 && b.total_invoices === 0) return -1;
        if (a.total_invoices === 0 && b.total_invoices > 0) return 1;
        return a.name.localeCompare(b.name);
      });
    },
  });
}
